import { useCallback, useMemo } from "react";

import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { getUnitListQueryDocument } from "./unit.gql";

export interface UseGetUnitListProps {
  workspaceId?: string;
  enabled?: boolean;
}

export const useGetUnitList = ({ workspaceId, enabled = true }: UseGetUnitListProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.units.list(workspaceId ?? ""),
    queryFn: async () => {
      if (!workspaceId) {
        throw new Error("Workspace ID is required");
      }
      const client = createOperationClient("GetUnitList");
      const data = await client.request(getUnitListQueryDocument, {
        input: { workspaceId },
      });
      return data.unitList;
    },
    enabled: enabled && !!workspaceId,
    staleTime: 2 * 60 * 1000, // 2 minutes for list data
  });

  const unitList = useMemo(() => {
    const maybeUnitList = queryResult.data ?? [];
    if (!maybeUnitList) {
      return [];
    }
    return maybeUnitList;
  }, [queryResult.data]);

  const findUnitById = useCallback(
    (id: string) => {
      return unitList.find((p) => p.id === id);
    },
    [unitList],
  );

  return {
    queryResult,
    unitList,
    loading: queryResult.isLoading,
    error: queryResult.error,
    findUnitById,
  };
};
